import type { LicenseSourceType } from './license-source-type';
import {
	LICENSE_SOURCE_TYPE_LABEL,
	LICENSE_SOURCE_TYPES,
} from './license-source-type';

/**
 * PD-16077 — the per-license-type client-list fields.
 *
 * The client list can show a client's license NUMBER and EXPIRATION DATE for
 * any canonical license type ("DEA License" / "DEA Expiration Date", "State
 * Board License" / "State Board Expiration Date", …). Neither value is a
 * `client` column: a client's licenses live on `client_license`, reached
 * through `client_address`, so each field is a correlated scalar subquery the
 * server selects ONLY when the column (or a sort on it) asks for it. See
 * `buildClientLicenseFieldSql` in apps/crm-api.
 *
 * Everything below is GENERATED from `LICENSE_SOURCE_TYPES` — adding a license
 * type there gives it a pair of list fields with no further edits here, in the
 * list-builder config, or in the clients service.
 *
 * Naming note: none of the generated names may contain a `gp` / `margin` /
 * `profit` token, or `GpStripInterceptor` would silently delete them from the
 * response for users without `orders:gp:view` (see CLAUDE.md → Gross Profit
 * Visibility). The `<type>License…` shape keeps them clear of the detector.
 */

/** Which of the two values a generated field carries. */
export type ClientLicenseListFieldKind = 'number' | 'expirationDate';

export interface ClientLicenseListFieldGroupModel {
	/** Canonical source type this pair reports on. */
	sourceType: LicenseSourceType;
	/** camelCase stem derived from the source type — e.g. `stateBoard`. */
	key: string;
	/** Human label stem — the canonical display label, e.g. `State Board`. */
	label: string;
	/** Field name for the license number — e.g. `deaLicenseNumber`. */
	numberField: string;
	/** Field name for the expiration date — e.g. `deaLicenseExpirationDate`. */
	expirationField: string;
	/** Picker label for the number field — e.g. `DEA License`. */
	numberLabel: string;
	/** Picker label for the expiration field — e.g. `DEA Expiration Date`. */
	expirationLabel: string;
	/**
	 * The stored `client_license.type` values that count as this type.
	 *
	 * The canonical stored form is the TITLE-CASE label (`'State Board'`), but
	 * the SCREAMING_SNAKE form was briefly written to tenant databases in 2026
	 * (see the incident note on `LICENSE_SOURCE_TYPES`), so both are accepted.
	 *
	 * Deliberately NOT included: the free-text legacy strays
	 * `normalizeLicenseSourceType()` maps heuristically (a state name such as
	 * `'Florida'`, anything containing `'PHARMACY'`). Those need the TypeScript
	 * normalizer, which SQL cannot run, and they are un-backfilled data rather
	 * than a form we write. A list column is a display convenience; cleaning
	 * those rows up is a data task, not a query workaround.
	 */
	matchValues: ReadonlyArray<string>;
}

export interface ClientLicenseListFieldModel {
	fieldName: string;
	fieldLabel: string;
	kind: ClientLicenseListFieldKind;
	group: ClientLicenseListFieldGroupModel;
}

/**
 * `STATE_BOARD` -> `stateBoard`, `DEA` -> `dea`, `NPI` -> `npi`. Acronyms stay
 * lower-cased as a whole so the field reads `deaLicenseNumber`, not
 * `dEALicenseNumber`.
 */
function toCamelCaseKey(sourceType: LicenseSourceType): string {
	return sourceType
		.toLowerCase()
		.split('_')
		.map((part, index) =>
			index === 0 ? part : `${part.charAt(0).toUpperCase()}${part.slice(1)}`,
		)
		.join('');
}

function buildGroup(
	sourceType: LicenseSourceType,
): ClientLicenseListFieldGroupModel {
	const key = toCamelCaseKey(sourceType);
	const label = LICENSE_SOURCE_TYPE_LABEL[sourceType];

	return {
		sourceType,
		key,
		label,
		numberField: `${key}LicenseNumber`,
		expirationField: `${key}LicenseExpirationDate`,
		numberLabel: `${label} License`,
		expirationLabel: `${label} Expiration Date`,
		matchValues: label === sourceType ? [label] : [label, sourceType],
	};
}

/** One entry per canonical license type, in `LICENSE_SOURCE_TYPES` order. */
export const CLIENT_LICENSE_LIST_FIELD_GROUPS: ReadonlyArray<ClientLicenseListFieldGroupModel> =
	LICENSE_SOURCE_TYPES.map(buildGroup);

/** Every generated field (two per type), in picker order. */
export const CLIENT_LICENSE_LIST_FIELDS: ReadonlyArray<ClientLicenseListFieldModel> =
	CLIENT_LICENSE_LIST_FIELD_GROUPS.flatMap((group) => [
		{
			fieldName: group.numberField,
			fieldLabel: group.numberLabel,
			kind: 'number' as const,
			group,
		},
		{
			fieldName: group.expirationField,
			fieldLabel: group.expirationLabel,
			kind: 'expirationDate' as const,
			group,
		},
	]);

const FIELD_BY_NAME: ReadonlyMap<string, ClientLicenseListFieldModel> = new Map(
	CLIENT_LICENSE_LIST_FIELDS.map((field) => [field.fieldName, field]),
);

/**
 * Resolve a field name to its definition, or `null` when it is not one of
 * these. Accepts the bare name (`deaLicenseNumber`) or the list-builder's
 * qualified form (`client.deaLicenseNumber`).
 */
export function getClientLicenseListField(
	fieldName: string | null | undefined,
): ClientLicenseListFieldModel | null {
	if (fieldName == null) {
		return null;
	}

	const bare = fieldName.includes('.')
		? fieldName.slice(fieldName.lastIndexOf('.') + 1)
		: fieldName;

	return FIELD_BY_NAME.get(bare) ?? null;
}

/**
 * True when the field is one of these computed license fields — i.e. it must
 * NOT be passed through as a `client.<column>` selection or ORDER BY.
 */
export function isClientLicenseListField(
	fieldName: string | null | undefined,
): boolean {
	return getClientLicenseListField(fieldName) != null;
}
